import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Trash2, CreditCard } from 'lucide-react';
import Layout from '../components/Layout';
import installmentService from '../services/installmentService';
import InstallmentForm from '../components/InstallmentForm';

export default function InstallmentsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<any | null>(null);
  const [showForm, setShowForm] = React.useState(false);
  const [showFinished, setShowFinished] = React.useState(false);
  const [filterMonth, setFilterMonth] = React.useState<number | null>(null);
  const [filterYear, setFilterYear] = React.useState<number>(new Date().getFullYear());

  const { data, isLoading } = useQuery({
    queryKey: ['installments'],
    queryFn: () => installmentService.list(),
  });

  // Calcular anos únicos disponíveis nas parcelas
  const availableYears = React.useMemo(() => {
    if (!data) return [new Date().getFullYear(), new Date().getFullYear() + 1];
    const years = new Set<number>();
    (data as any[]).forEach((it) => {
      if (it.startYear) {
        years.add(Number(it.startYear));
      }
    });
    const currentYear = new Date().getFullYear();
    years.add(currentYear);
    years.add(currentYear + 1);
    return Array.from(years).sort((a, b) => a - b);
  }, [data]);

  const filteredData = React.useMemo(() => {
    if (!data) return [];

    const withinRange = (it: any) => {
      const count = it.installmentCount != null ? Number(it.installmentCount) : 1;

      // Preferir transações para determinar o período real (inclusive quando começa em outro ano)
      const txs: any[] = Array.isArray(it.transactions) ? it.transactions : [];
      const parseMY = (t: any) => {
        const d = t.dueDate || t.date ? new Date(t.dueDate || t.date) : null;
        const m = t.month != null ? Number(t.month) : (d ? d.getMonth() + 1 : null);
        const y = t.year != null ? Number(t.year) : (d ? d.getFullYear() : null);
        return m && y ? { m, y } : null;
      };

      if (txs.length) {
        // Verifica interseção com o ano/mês filtrado usando as transações geradas
        if (filterMonth === null) {
          // Qualquer transação no ano filtrado
          return txs.some((t) => {
            const p = parseMY(t);
            return p ? p.y === filterYear : false;
          });
        }
        // Mês específico
        return txs.some((t) => {
          const p = parseMY(t);
          return p ? p.m === filterMonth && p.y === filterYear : false;
        });
      }

      // Fallback: calcular o intervalo apenas pelo início + quantidade de parcelas
      const startMonth = it.startMonth != null ? Number(it.startMonth) : 1;
      const startYear = it.startYear != null ? Number(it.startYear) : new Date().getFullYear();
      const startDate = new Date(startYear, Math.max(0, startMonth - 1), 1);
      const endDate = new Date(startYear, Math.max(0, startMonth - 1) + Math.max(0, count - 1), 1);

      if (filterMonth === null) {
        // Mostrar se o ano filtrado está entre o ano inicial e final do parcelamento
        const minYear = Math.min(startDate.getFullYear(), endDate.getFullYear());
        const maxYear = Math.max(startDate.getFullYear(), endDate.getFullYear());
        return filterYear >= minYear && filterYear <= maxYear;
      }

      const filterDate = new Date(filterYear, filterMonth - 1, 1);
      return filterDate.getTime() >= startDate.getTime() && filterDate.getTime() <= endDate.getTime();
    };

    let result = (data as any[]).filter(withinRange);

    // Ordenar: parcelas não finalizadas primeiro, depois finalizadas
    result = result.sort((a: any, b: any) => {
      const aIsFinished = (a.paidCount || 0) >= (a.installmentCount || 1);
      const bIsFinished = (b.paidCount || 0) >= (b.installmentCount || 1);
      
      if (aIsFinished === bIsFinished) return 0;
      return aIsFinished ? 1 : -1;
    });

    // Aplicar filtro de mostrar/esconder finalizadas
    if (!showFinished) {
      result = result.filter((it: any) => {
        const isFinished = (it.paidCount || 0) >= (it.installmentCount || 1);
        return !isFinished;
      });
    }
    
    return result;
  }, [data, showFinished, filterMonth, filterYear]);

  const createMutation = useMutation({
    mutationFn: (payload: any) => installmentService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['installments'] });
      setShowForm(false);
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Parcelamento criado com sucesso!' } }));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: any) => installmentService.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['installments'] });
      setEditing(null);
      setShowForm(false);
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Parcelamento atualizado com sucesso!' } }));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => installmentService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['installments'] });
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Parcelamento excluído com sucesso!' } }));
    },
  });

  const onSubmit = async (payload: any) => {
    if (editing && editing.id) {
      await updateMutation.mutateAsync({ id: editing.id, payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'BRL' }).format(value || 0);

  const renderProgress = (it: any) => {
    const paid = it.paidCount || 0;
    const total = it.installmentCount || 1;
    const percent = Math.min(100, Math.round((paid / total) * 100));
    return (
      <div className="mt-3">
        <div className="text-xs text-gray-500">Progresso</div>
        <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
          <div className="h-2 bg-emerald-500" style={{ width: `${percent}%` }} />
        </div>
        <div className="mt-1 text-xs text-gray-500">{paid} de {total} pagas</div>
      </div>
    );
  };

  const renderNextParcels = (it: any) => {
    const paid = it.paidCount || 0;
    const total = it.installmentCount || 1;
    const isFinished = paid >= total;
    
    const transactions = it.transactions || [];
    
    if (isFinished) {
      // Encontrar a última transação paga para exibir "Quitado em"
      const paidTransactions = transactions
        .filter((t: any) => t.isPaid)
        .sort((a: any, b: any) => new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime());
      
      if (paidTransactions.length > 0) {
        const lastPaidDate = new Date(paidTransactions[0].dueDate);
        const monthName = lastPaidDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
        return (
          <div className="mt-3">
            <div className="text-xs font-semibold text-green-700 bg-green-50 border border-green-100 rounded px-2 py-1 inline-block">
              ✓ Quitado em {monthName}
            </div>
          </div>
        );
      }
      
      return (
        <div className="mt-3">
          <div className="text-xs text-gray-500 mb-1">Status:</div>
          <div className="text-xs text-gray-500 italic">Finalizado</div>
        </div>
      );
    }
    
    // Parcelas não finalizadas - mostrar próximas em aberto
    const nextParcels = transactions
      .filter((t: any) => !t.isPaid)
      .sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
      .slice(0, 3);

    return (
      <div className="mt-3">
        <div className="text-xs text-gray-500 mb-1">Próximas Parcelas em Aberto:</div>
        <div className="flex gap-2 flex-wrap">
          {nextParcels.map((t: any, i: number) => {
            const date = new Date(t.dueDate);
            const label = date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
            return (
              <div
                key={t.id || i}
                className="bg-amber-50 text-amber-800 rounded px-2 py-1 border border-amber-100 text-xs whitespace-nowrap"
              >
                <span className="font-medium capitalize">{label}</span>
                <span className="mx-1">•</span>
                <span className="font-semibold">{formatCurrency(t.amount)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Layout title="Parcelas" icon={CreditCard}>
      <div className="flex flex-col h-full bg-gray-100">
        <div className="hidden md:block bg-white border-b border-gray-200 px-4 md:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Parcelas</h1>
                <p className="text-gray-600 text-sm mt-1">Cadastre compras parceladas de forma inteligente (ex: 3x de R$ 140,00)</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-b border-gray-200 px-4 md:px-8 py-2 md:py-4 flex-shrink-0">
          <div className="flex justify-start mb-2 md:mb-4">
            <button
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
              className="bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 font-medium text-sm w-full md:w-auto"
            >
              + Nova Parcela
            </button>
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                checked={showFinished}
                onChange={(e) => setShowFinished(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-gray-900"
              />
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Exibir finalizadas</span>
            </label>
            
            <div className="flex gap-2 flex-wrap md:flex-nowrap items-center">
              <select 
                value={filterMonth ?? ''} 
                onChange={(e) => setFilterMonth(e.target.value ? Number(e.target.value) : null)}
                className="border border-gray-300 px-3 py-2 rounded-md focus:outline-none appearance-none text-gray-900 bg-white text-sm w-48 md:w-56 capitalize"
              >
                <option value="">Todos os meses</option>
                <option value="1">Janeiro</option>
                <option value="2">Fevereiro</option>
                <option value="3">Março</option>
                <option value="4">Abril</option>
                <option value="5">Maio</option>
                <option value="6">Junho</option>
                <option value="7">Julho</option>
                <option value="8">Agosto</option>
                <option value="9">Setembro</option>
                <option value="10">Outubro</option>
                <option value="11">Novembro</option>
                <option value="12">Dezembro</option>
              </select>
              
              <select 
                value={filterYear ?? ''} 
                onChange={(e) => setFilterYear(Number(e.target.value))}
                className="border border-gray-300 px-3 py-2 rounded-md focus:outline-none appearance-none text-gray-900 bg-white text-sm w-28"
              >
                {availableYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6 space-y-6">
          {showForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-100">
                <div className="flex justify-between items-start p-6 border-b border-gray-200">
                  <div>
                    <h2 className="text-lg font-bold">{editing ? 'Editar Parcelamento' : 'Criar Parcelamento'}</h2>
                    <p className="text-sm text-gray-600">Exemplo: 3x de R$ 140,00 = R$ 420,00</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditing(null);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label="Fechar"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-6">
                  <InstallmentForm
                    initial={editing ?? undefined}
                    onCancel={() => {
                      setShowForm(false);
                      setEditing(null);
                    }}
                    onSubmit={onSubmit}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow border border-gray-100 p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Parcelamentos Cadastrados</h2>
              <span className="text-sm text-gray-500">({filteredData.length})</span>
            </div>

            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : filteredData?.length ? (
              <div className="space-y-4">
                {filteredData.map((it: any) => (
                  <div key={it.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center font-semibold">
                          {it.description?.[0]?.toUpperCase() || 'P'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-gray-900">{it.description || 'Parcelamento'}</h3>
                            <span className="px-3 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">{it.category || 'Compras'}</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            Valor Total {formatCurrency(it.totalAmount || 0)} • {it.installmentCount}x
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-6 text-sm text-gray-700">
                        <div>
                          <div className="text-xs text-gray-500">Parcelas</div>
                          <div className="font-semibold">{it.installmentCount}x</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Valor Parcela</div>
                          <div className="font-semibold">{formatCurrency(it.installmentValue || 0)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Início</div>
                          <div className="font-semibold">{new Date((it.startYear || new Date().getFullYear()), (it.startMonth || 1) - 1, 1).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              // Duplicar: abrir modal com dados preenchidos (sem ID para criar novo)
                              setEditing({ ...it, id: undefined });
                              setShowForm(true);
                            }}
                            className="p-2 rounded hover:bg-gray-100 text-gray-700"
                            title="Duplicar"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Tem certeza que deseja excluir?')) deleteMutation.mutate(it.id);
                            }}
                            className="p-2 rounded hover:bg-gray-100 text-red-600"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {renderProgress(it)}
                    {renderNextParcels(it)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500">Nenhum parcelamento cadastrado</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
